<template>
    <span>
        <v-tooltip bottom v-if="iconOnly">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    small
                    :disabled="disabled"
                    :loading="loading"
                    v-bind="attrs"
                    v-on="on"
                    @click="openDialog"
                >
                    <v-icon size="18" color="primary">mdi-robot-outline</v-icon>
                </v-btn>
            </template>
            <span>{{ label }}</span>
        </v-tooltip>

        <v-btn
            v-else
            small
            outlined
            color="primary"
            elevation="0"
            :disabled="disabled"
            :loading="loading"
            @click="openDialog"
        >
            <v-icon left small>mdi-robot-outline</v-icon>
            {{ label }}
        </v-btn>

        <v-dialog v-model="dialog" max-width="900px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ title }}</h3>
                        <p class="ui-section-description">Análise assistida por IA com base nos dados disponíveis.</p>
                    </div>
                    <v-btn icon small :disabled="loading" @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>

                <div class="ui-card__body">
                    <div v-if="loading" class="ai-analysis-loading">
                        <v-progress-circular indeterminate color="primary" size="36" width="3" />
                        <span>Consultando a IA...</span>
                    </div>

                    <v-alert v-else-if="error" dense text type="error" class="mb-0">
                        {{ error }}
                    </v-alert>

                    <template v-else-if="result">
                        <div class="ai-answer-box">
                            <div class="ai-section-label">Resposta</div>
                            <div class="white-space-pre-line">{{ result.answer || result.summary || "Sem resposta." }}</div>
                        </div>

                        <div v-if="result.summary && result.summary !== result.answer" class="ai-answer-box">
                            <div class="ai-section-label">Resumo</div>
                            <div class="white-space-pre-line">{{ result.summary }}</div>
                        </div>

                        <v-alert v-if="result.warnings && result.warnings.length" dense text type="warning">
                            <ul class="ai-list">
                                <li v-for="(item, index) in result.warnings" :key="`warning-${index}`">{{ item }}</li>
                            </ul>
                        </v-alert>

                        <div v-if="result.pending_items && result.pending_items.length" class="ai-answer-box">
                            <div class="ai-section-label">Pendências</div>
                            <ul class="ai-list">
                                <li v-for="(item, index) in result.pending_items" :key="`pending-${index}`">{{ item }}</li>
                            </ul>
                        </div>

                        <div v-if="result.suggested_actions && result.suggested_actions.length" class="ai-answer-box">
                            <div class="ai-section-label">Ações sugeridas</div>
                            <ul class="ai-list">
                                <li v-for="(item, index) in result.suggested_actions" :key="`action-${index}`">{{ item }}</li>
                            </ul>
                        </div>

                        <div v-if="result.sources && result.sources.length" class="ai-source-row">
                            <ui-badge
                                v-for="(source, index) in result.sources"
                                :key="`source-${index}`"
                                tone="primary"
                                :dot="false"
                                :text="source.title || source.id || source.type || 'Fonte'"
                            />
                        </div>

                        <div class="ai-meta-row">
                            <span v-if="result.confidence">Confiança: {{ result.confidence }}</span>
                            <span v-if="result.usage">Modelo: {{ result.usage.model || "N/A" }}</span>
                            <span v-if="result.usage && result.usage.estimated_cost_usd !== undefined">
                                Custo estimado: US$ {{ Number(result.usage.estimated_cost_usd).toFixed(6) }}
                            </span>
                        </div>
                    </template>

                    <v-alert v-else dense text type="info" class="mb-0">
                        Clique em analisar para consultar a IA.
                    </v-alert>
                </div>

                <div class="ui-card__footer">
                    <v-btn text :disabled="loading" @click="dialog = false">Fechar</v-btn>
                    <v-btn color="primary" depressed :loading="loading" @click="runAnalysis">
                        <v-icon left small>mdi-refresh</v-icon>
                        Analisar novamente
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </span>
</template>

<script>
import axios from "axios";

export default {
    name: "AIAnalysisButton",

    props: {
        endpoint: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: "Analisar com IA",
        },
        title: {
            type: String,
            default: "Análise IA",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        dialog: false,
        loading: false,
        result: null,
        error: null,
    }),

    methods: {
        openDialog() {
            this.dialog = true;
            this.runAnalysis();
        },

        async runAnalysis() {
            if (!this.endpoint) return;

            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post(this.endpoint);
                this.result = response.data.content || {};
            } catch (error) {
                const data = error.response && error.response.data ? error.response.data : {};
                this.error = data.message
                    || (data.content && data.content.error && data.content.error.message)
                    || (data.error && data.error.message)
                    || "Não foi possível executar a análise com IA.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import "../assets/scss/ui/tokens";

.ai-analysis-loading {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: $ui-muted;
}

.ai-answer-box {
    background: $ui-bg-muted;
    border: 1px solid $ui-border;
    border-radius: $ui-radius-sm;
    padding: 14px;
    margin-bottom: 12px;
    color: $ui-text;
}

.ai-section-label {
    font-size: 12px;
    font-weight: 600;
    color: $ui-muted;
    margin-bottom: 6px;
}

.ai-list {
    margin: 0;
    padding-left: 18px;
}

.ai-source-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.ai-meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    font-size: 12px;
    color: $ui-muted;
}

.white-space-pre-line {
    white-space: pre-line;
}
</style>
